import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { withAppHOC } from '../../../hoc';
import PageHeading from '../../../components/PageHeader/PageHeader';
import LoadingSpinner from '../../../components/LoadingSpinner/LoadingSpinner';
import NoData from '../../../components/NoData/NoData';
import {
  MessageBox,
  Form,
  Fieldset,
  TextInput,
  ResetButton,
  Button,
  Label,
  Dropdown,
} from '../../../components/FormElements';
import { TableBuilder } from '../../../components/TableElements';
import Pagination from '../../../components/Pagination/Pagination';
import { StyledFiltersContainer } from '../../../components/Styled';
import API from '../../../api';
import { API_RESPONSE_TYPES } from '../../../constants';
import {
  getSearchParams,
  formatCurrency,
  shouldRedirect,
  redirectToPageOne,
} from '../../../utils';
import FiltersButton from '../../../components/FiltersButton/FiltersButton';

const UsersWalletTransactionsList = (props) => {
  const [paginationData, setPaginationData] = useState({
    pageNumber: getSearchParams('pageNumber') || 1,
    recordsPerPage: getSearchParams('recordsPerPage') || 10,
  });
  const [totalRecords, setTotalRecords] = useState(0);
  const [isFiltering, setIsFiltering] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [responseStatus, setResponseStatus] = useState('');
  const [responseMessage, setResponseMessage] = useState('');
  const [walletTransactions, setWalletTransactions] = useState([]);
  const [profileType, setProfileType] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const { id } = useParams();

  const resetForm = () => {
    setProfileType('');
    setFromDate('');
    setToDate('');
    readData();
  };

  const readData = useCallback(
    (profileType = '', fromDate = '', toDate = '') => {
      setIsLoading(true);
      setResponseStatus('');
      setResponseMessage('');
      API.get(
        `/accounts-management/users/wallet-transactions/${id}?pageNumber=${paginationData.pageNumber}&recordsPerPage=${paginationData.recordsPerPage}&profileType=${profileType}&fromDate=${fromDate}&toDate=${toDate}`
      )
        .then((response) => {
          const { status, message, data, pageInfo } = response.data;
          if (shouldRedirect(data?.length, paginationData?.pageNumber)) {
            redirectToPageOne();
          } else {
            if (status === API_RESPONSE_TYPES.FAILED) {
              setResponseStatus(status);
              setResponseMessage(message);
            } else {
              setWalletTransactions(data);
              setTotalRecords(pageInfo.totalRecords);
            }
          }
        })
        .catch((error) => {
          setResponseStatus(API_RESPONSE_TYPES.FAILED);
          setResponseMessage(error.message);
        })
        .finally(() => {
          setIsLoading(false);
        });
    },
    [paginationData, id]
  );

  const onClickExport = () => {
    setIsLoading(true);
    setResponseStatus('');
    setResponseMessage('');
    API.get(
      `/accounts-management/users/wallet-transactions-export/${id}?profileType=${profileType}&fromDate=${fromDate}&toDate=${toDate}`
    )
      .then((response) => {
        const { status, message } = response.data;
        setResponseStatus(status);
        setResponseMessage(message);
        setTimeout(() => {
          setResponseStatus('');
          setResponseMessage('');
        }, 3000);
      })
      .catch((error) => {
        setResponseStatus(API_RESPONSE_TYPES.FAILED);
        setResponseMessage(error.message);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const reloadData = () => {
    readData(profileType, fromDate, toDate);
  };

  const filterData = (e) => {
    e.preventDefault();
    setIsFiltering(true);
  };

  useEffect(() => {
    if (isFiltering) {
      setPaginationData((paginationData) => ({
        pageNumber: 1,
        recordsPerPage: paginationData.recordsPerPage,
      }));
      setIsFiltering((isFiltering) => !isFiltering);
    }
  }, [isFiltering]);

  useEffect(() => {
    if (!isFiltering) {
      readData(profileType, fromDate, toDate);
    }
  }, [paginationData, readData]);

  const [showFilters, setShowFilters] = useState(false);
  const onClickShowFilters = () => {
    setShowFilters((showFilters) => !showFilters);
  };

  return (
    <>
      <PageHeading
        {...props}
        canReloadProp={true}
        reloadData={reloadData}
        showExport={walletTransactions.length > 0}
        onClickExport={onClickExport}
      />
      <FiltersButton onClick={onClickShowFilters} value={showFilters} />
      <StyledFiltersContainer className={showFilters ? 'show' : ''}>
        <Form method="POST" action="#" onSubmit={filterData}>
          <Fieldset>
            <Label>Profile Type</Label>
            <Dropdown
              placeholder="Please select profile type"
              value={profileType}
              onChange={setProfileType}
              options={[
                {
                  title: '-- SELECT PROFILE TYPE --',
                  value: '',
                },
                {
                  title: 'Buyer',
                  value: 'Buyer',
                },
                {
                  title: 'Seller',
                  value: 'Seller',
                },
              ]}
              disabled={isLoading}
            />
          </Fieldset>
          <Fieldset>
            <Label>Start Date</Label>
            <TextInput
              type="date"
              value={fromDate}
              onChange={setFromDate}
              placeholder="Start Date"
              disabled={isLoading}
            />
          </Fieldset>
          <Fieldset>
            <Label>End Date</Label>
            <TextInput
              type="date"
              value={toDate}
              onChange={setToDate}
              placeholder="End Date"
              disabled={isLoading}
            />
          </Fieldset>
          <Fieldset>
            <Button disabled={isLoading} type="submit">
              Filter
            </Button>
            <ResetButton disabled={isLoading} type="button" onClick={resetForm}>
              Reset
            </ResetButton>
          </Fieldset>
        </Form>
      </StyledFiltersContainer>
      <LoadingSpinner
        isLoading={
          responseStatus === '' && walletTransactions?.length === 0 && isLoading
        }
      />
      <NoData
        status={
          responseStatus !== API_RESPONSE_TYPES.FAILED &&
          !isLoading &&
          walletTransactions?.length === 0
        }
        message={`No wallet transactions found`}
      />
      {responseStatus && responseMessage && (
        <MessageBox status={responseStatus} message={responseMessage} />
      )}
      {walletTransactions?.length > 0 && (
        <>
          <TableBuilder
            isLoading={walletTransactions?.length !== 0 && isLoading}
            tableHeadings={[
              {
                title: 'Transactions Id',
                dataSelector: 'transactionsId',
                dataType: 'string',
                CellRenderer: (value) => value ?? 'N/A',
              },
              {
                title: 'Profile Type',
                dataSelector: 'profileType',
                dataType: 'string',
              },
              {
                title: 'Wallet Type',
                dataSelector: 'walletType',
                dataType: 'string',
              },
              {
                title: 'Transaction Type',
                dataSelector: 'transactionType',
                dataType: 'string',
              },
              {
                title: 'Amount',
                dataSelector: 'amount',
                dataType: 'number',
                CellRenderer: (value, item) => (
                  <span
                    style={{
                      color:
                        item.transactionType === 'Credit' ? 'green' : 'red',
                    }}
                  >
                    {formatCurrency(value)}
                  </span>
                ),
              },
              {
                title: 'Balance',
                dataSelector: 'balance',
                dataType: 'number',
                CellRenderer: (value) => formatCurrency(value),
              },
              {
                title: 'Remark',
                dataSelector: 'remark',
                dataType: 'string',
                CellRenderer: (value) => value || 'N/A',
              },
              {
                title: 'Date & Time',
                dataSelector: 'createdOn',
                dataType: 'string',
              },
            ]}
            tableData={walletTransactions}
          />
          <Pagination
            totalRecords={totalRecords}
            paginationData={paginationData}
            setPaginationData={setPaginationData}
          />
        </>
      )}
    </>
  );
};

export default withAppHOC(UsersWalletTransactionsList);
